import * as THREE from "three";
import { SectorPosition } from "./coordinates";
import { createCubeSpherePlanet, type PlanetVisual } from "./cube-sphere";
import { LocalPlanetPatch } from "./local-patch";
import { terrainHeight } from "./planet-terrain";
import { NetworkClient } from "./network";
import { SectorGrid } from "./sector-grid";
import type { PlanetConfig, PlanetDefinition, PlacedBlockWire, PlayerWire, ServerMessage } from "./types";

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = THREE.MathUtils.clamp((value - edge0) / Math.max(1e-6, edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

interface PlanetRuntime {
  definition: PlanetDefinition;
  position: SectorPosition;
  visual: PlanetVisual;
}

interface DebugState {
  ready: boolean;
  mode: "space" | "surface";
  activePlanet: string;
  sector: [number, number, number];
  localOffset: [number, number, number];
  altitude: number;
  curvature: number;
  localChunks: number;
  planetMeshes: number;
  players: number;
  blocks: number;
  fps: number;
  rttMs: number;
  webglError: number;
  contextLost: boolean;
}

declare global {
  interface Window {
    __PLANET_SMOKE__?: boolean;
    __PLANET_EXPERIMENT__?: DebugState & {
      jumpToPlanet: (id: string) => void;
      enterSurface: () => void;
      enterSpace: () => void;
      setDemoAltitude: (altitude: number) => void;
      toggleGrid: () => void;
      setPaused: (paused: boolean) => void;
      renderOnce: () => void;
    };
  }
}

const BLOCK_COLORS: Record<PlacedBlockWire["block"], string> = {
  grass: "#4d9148",
  stone: "#747c84",
  glass: "#87d9f2",
};

export class PlanetGame {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(70, 1, 0.1, 800_000);
  private readonly planetRoot = new THREE.Group();
  private readonly remoteRoot = new THREE.Group();
  private readonly blockRoot = new THREE.Group();
  private readonly sectorGrid: SectorGrid;
  private readonly localPatch: LocalPlanetPatch;
  private readonly planets = new Map<string, PlanetRuntime>();
  private readonly players = new Map<string, PlayerWire>();
  private readonly remoteMeshes = new Map<string, THREE.Mesh>();
  private readonly blockMeshes = new Map<string, THREE.Mesh>();
  private readonly blocks = new Map<string, PlacedBlockWire>();
  private readonly keys = new Set<string>();
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2(0, 0);
  private readonly clock = new THREE.Clock();
  private readonly statusLines = document.querySelector<HTMLElement>("#status-lines")!;
  private readonly planetName = document.querySelector<HTMLElement>("#planet-name")!;
  private readonly planetMode = document.querySelector<HTMLElement>("#planet-mode")!;
  private readonly toastElement = document.querySelector<HTMLElement>("#toast")!;
  private observer: SectorPosition;
  private activePlanetId = "gaia";
  private mode: "space" | "surface" = "space";
  private yaw = 0;
  private pitch = 0;
  private speed = 1_200;
  private curvature = 1;
  private altitude = 0;
  private fps = 0;
  private frameCounter = 0;
  private frameAccumulator = 0;
  private gridVisible = true;
  private selectedBlock: PlacedBlockWire["block"] = "grass";
  private contextLost = false;
  private ready = false;
  private demoAltitudes = [160, 1_150, 2_800];
  private demoIndex = 0;
  private paused = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly config: PlanetConfig,
    private readonly network: NetworkClient,
  ) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.7));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.AgXToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.renderer.shadowMap.enabled = false;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.scene.background = new THREE.Color("#020711");

    for (const definition of config.planets) {
      const visual = createCubeSpherePlanet(definition, window.__PLANET_SMOKE__ ? 12 : 32);
      const position = new SectorPosition(definition.sector, definition.localOffsetMeters, config.sectorSizeMeters);
      this.planets.set(definition.id, { definition, position, visual });
      this.planetRoot.add(visual.group);
    }
    const active = this.getActivePlanet();
    this.localPatch = new LocalPlanetPatch(active.definition, config.localPatch);
    this.localPatch.group.visible = false;
    this.sectorGrid = new SectorGrid(config.sectorSizeMeters, 2);
    this.scene.add(this.planetRoot, this.localPatch.group, this.remoteRoot, this.blockRoot, this.sectorGrid.group);

    const gaia = this.planets.get("gaia") ?? active;
    this.observer = gaia.position.clone().translate(new THREE.Vector3(0, 2_000, 16_000));
    this.camera.rotation.order = "YXZ";

    this.installLights();
    this.installStars();
    this.installInput();
    this.resize();
    this.network.setHandler((message) => this.handleMessage(message));
    this.renderer.setAnimationLoop(() => this.animate());
    this.ready = true;
    this.publishDebug();
  }

  jumpToPlanet(id: string): void {
    const runtime = this.planets.get(id);
    if (!runtime) return;
    this.activePlanetId = id;
    const radius = runtime.definition.diameterMeters * 0.5;
    this.observer = runtime.position.clone().translate(new THREE.Vector3(0, radius * 0.38, radius * 3.25));
    this.mode = "space";
    this.camera.position.set(0, 0, 0);
    this.toast(`Орбита ${runtime.definition.name}`);
  }

  enterSurface(): void {
    const runtime = this.getActivePlanet();
    const fromCenter = this.observer.relativeTo(runtime.position);
    const candidates = [
      fromCenter.lengthSq() > 1 ? fromCenter.normalize() : new THREE.Vector3(0.25, 0.92, 0.3).normalize(),
      new THREE.Vector3(0.25, 0.92, 0.3).normalize(),
      new THREE.Vector3(-0.42, 0.81, 0.39).normalize(),
      new THREE.Vector3(0.63, 0.67, -0.38).normalize(),
      new THREE.Vector3(-0.55, 0.58, -0.60).normalize(),
      new THREE.Vector3(0.12, -0.86, 0.50).normalize(),
    ];
    let normal = candidates[0]!.clone();
    let bestHeight = terrainHeight(normal, runtime.definition);
    for (const candidate of candidates.slice(1)) {
      const height = terrainHeight(candidate, runtime.definition);
      if (height > bestHeight) { bestHeight = height; normal = candidate.clone(); }
    }
    this.localPatch.setPlanet(runtime.definition, normal);
    const ground = this.localPatch.groundHeightAtLocal(0, 160);
    this.camera.position.set(0, ground + 120, 160);
    this.yaw = 0;
    this.pitch = -0.52;
    this.camera.rotation.set(this.pitch, this.yaw, 0, "YXZ");
    this.mode = "surface";
    this.demoIndex = 0;
    this.toast(`Локальная плоскость ${runtime.definition.name}`);
  }

  enterSpace(): void {
    const runtime = this.getActivePlanet();
    const radius = runtime.definition.diameterMeters * 0.5;
    const normal = this.localPatch.frame.normal;
    const distance = radius + Math.max(3_000, this.altitude + 1_500);
    this.observer = runtime.position.clone().translate(normal.clone().multiplyScalar(distance));
    this.camera.position.set(0, 0, 0);
    this.mode = "space";
    this.toast("Межпланетная система координат");
  }

  setDemoAltitude(altitude: number): void {
    if (this.mode !== "surface") this.enterSurface();
    const ground = this.localPatch.groundHeightAtLocal(this.camera.position.x, this.camera.position.z);
    this.camera.position.y = ground + Math.max(5, altitude);
  }

  toggleGrid(): void {
    this.gridVisible = !this.gridVisible;
    this.sectorGrid.setVisible(this.gridVisible && this.mode === "space");
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.renderer.setAnimationLoop(paused ? null : () => this.animate());
  }

  renderOnce(): void {
    const elapsed = this.clock.elapsedTime;
    this.updateScene(elapsed);
    this.updateRemotePlayers();
    this.updatePlacedBlocks();
    this.renderer.render(this.scene, this.camera);
    this.updateHud();
    this.publishDebug();
  }

  private installLights(): void {
    this.scene.add(new THREE.HemisphereLight("#a8d7ff", "#152014", 1.45));
    const sun = new THREE.DirectionalLight("#fff0cf", 3.2);
    sun.position.set(30_000, 45_000, 12_000);
    sun.castShadow = false;
    this.scene.add(sun);
  }

  private installStars(): void {
    const count = 5_000;
    const positions = new Float32Array(count * 3);
    const random = (index: number): number => {
      const x = Math.sin(index * 12.9898 + 78.233) * 43_758.5453;
      return x - Math.floor(x);
    };
    for (let i = 0; i < count; i += 1) {
      const theta = random(i * 3) * Math.PI * 2;
      const cosPhi = random(i * 3 + 1) * 2 - 1;
      const sinPhi = Math.sqrt(1 - cosPhi * cosPhi);
      const radius = 260_000 + random(i * 3 + 2) * 80_000;
      positions[i * 3] = Math.cos(theta) * sinPhi * radius;
      positions[i * 3 + 1] = cosPhi * radius;
      positions[i * 3 + 2] = Math.sin(theta) * sinPhi * radius;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({ color: "#d9efff", size: 55, sizeAttenuation: true, transparent: true, opacity: 0.84, depthWrite: false });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.name = "stars";
    this.scene.add(points);
  }

  private installInput(): void {
    window.addEventListener("resize", () => this.resize());
    window.addEventListener("keydown", (event) => {
      this.keys.add(event.code);
      if (event.repeat) return;
      if (event.code === "Digit1") this.jumpToPlanet("gaia");
      else if (event.code === "Digit2") this.jumpToPlanet("ares");
      else if (event.code === "KeyL") this.enterSurface();
      else if (event.code === "KeyO") this.enterSpace();
      else if (event.code === "KeyG") this.toggleGrid();
      else if (event.code === "KeyP") {
        this.demoIndex = (this.demoIndex + 1) % this.demoAltitudes.length;
        this.setDemoAltitude(this.demoAltitudes[this.demoIndex]!);
      } else if (event.code === "Digit3") this.selectBlock("glass", 2);
    });
    window.addEventListener("keyup", (event) => this.keys.delete(event.code));
    window.addEventListener("blur", () => this.keys.clear());
    this.canvas.addEventListener("click", () => {
      if (document.pointerLockElement !== this.canvas) void this.canvas.requestPointerLock();
    });
    window.addEventListener("mousemove", (event) => {
      if (document.pointerLockElement !== this.canvas) return;
      this.yaw -= event.movementX * 0.0022;
      this.pitch = THREE.MathUtils.clamp(this.pitch - event.movementY * 0.0022, -1.52, 1.52);
    });
    this.canvas.addEventListener("wheel", (event) => {
      event.preventDefault();
      this.speed = THREE.MathUtils.clamp(this.speed * Math.exp(-event.deltaY * 0.0012), 8, 80_000);
      this.toast(`Скорость ${Math.round(this.speed)} м/с`);
    }, { passive: false });
    this.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.canvas.addEventListener("mousedown", (event) => {
      if (document.pointerLockElement !== this.canvas || this.mode !== "surface") return;
      if (event.button === 2) this.placeBlock();
      else if (event.button === 0) this.removeBlock();
    });
    this.canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      this.contextLost = true;
      this.publishDebug();
    });
    this.canvas.addEventListener("webglcontextrestored", () => {
      this.contextLost = false;
      this.publishDebug();
    });
    document.querySelectorAll<HTMLButtonElement>(".hotbar-slot").forEach((button) => {
      button.addEventListener("click", () => {
        const slot = Number(button.dataset.slot ?? 0);
        this.selectBlock(slot === 1 ? "stone" : slot === 2 ? "glass" : "grass", slot);
      });
    });
  }

  private selectBlock(block: PlacedBlockWire["block"], slot: number): void {
    this.selectedBlock = block;
    document.querySelectorAll(".hotbar-slot").forEach((element, index) => element.classList.toggle("selected", index === slot));
  }

  private animate(): void {
    const delta = Math.min(this.clock.getDelta(), 0.05);
    const elapsed = this.clock.elapsedTime;
    this.updateMovement(delta);
    this.updateScene(elapsed);
    this.updateRemotePlayers();
    this.updatePlacedBlocks();
    this.renderer.render(this.scene, this.camera);
    this.updateFps(delta);
    this.updateNetwork(performance.now());
    this.updateHud();
    this.publishDebug();
  }

  private updateMovement(delta: number): void {
    this.camera.rotation.set(this.pitch, this.yaw, 0, "YXZ");
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0);
    const movement = new THREE.Vector3();
    if (this.keys.has("KeyW")) movement.add(forward);
    if (this.keys.has("KeyS")) movement.sub(forward);
    if (this.keys.has("KeyD")) movement.add(right);
    if (this.keys.has("KeyA")) movement.sub(right);
    if (this.keys.has("Space")) movement.add(up);
    if (this.keys.has("KeyC")) movement.sub(up);
    if (movement.lengthSq() === 0) return;
    movement.normalize();
    const boost = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") ? 6 : 1;
    const speed = this.mode === "surface" ? Math.min(this.speed, 2_400) : this.speed;
    movement.multiplyScalar(speed * boost * delta);
    if (this.mode === "space") this.observer.translate(movement);
    else {
      this.camera.position.add(movement);
      const ground = this.localPatch.groundHeightAtLocal(this.camera.position.x, this.camera.position.z);
      this.camera.position.y = Math.max(this.camera.position.y, ground + 2.2);
      const horizontal = Math.hypot(this.camera.position.x, this.camera.position.z);
      if (horizontal > this.config.localPatch.reanchorDistanceMeters) this.reanchorSurface();
    }
  }

  private reanchorSurface(): void {
    const oldGround = this.localPatch.groundHeightAtLocal(this.camera.position.x, this.camera.position.z);
    const altitude = this.camera.position.y - oldGround;
    const radius = this.getActivePlanet().definition.diameterMeters * 0.5;
    const newNormal = this.localPatch.frame.normal.clone().multiplyScalar(radius)
      .addScaledVector(this.localPatch.frame.east, this.camera.position.x)
      .addScaledVector(this.localPatch.frame.north, this.camera.position.z)
      .normalize();
    this.localPatch.reanchor(newNormal);
    const newGround = this.localPatch.groundHeightAtLocal(0, 0);
    this.camera.position.set(0, newGround + altitude, 0);
  }

  private updateScene(elapsed: number): void {
    if (this.mode === "space") {
      this.scene.fog = null;
      this.scene.background = new THREE.Color("#020711");
      this.localPatch.group.visible = false;
      this.sectorGrid.setVisible(this.gridVisible);
      for (const runtime of this.planets.values()) {
        runtime.visual.group.visible = true;
        runtime.visual.group.matrixAutoUpdate = true;
        runtime.visual.group.position.copy(runtime.position.relativeTo(this.observer));
        runtime.visual.surface.material.transparent = false;
        runtime.visual.surface.material.opacity = 1;
        runtime.visual.ocean.material.opacity = 0.72;
        runtime.visual.atmosphere.material.uniforms.uOpacity!.value = 0.42;
      }
      this.curvature = 1;
      this.altitude = 0;
      return;
    }

    this.sectorGrid.setVisible(false);
    this.localPatch.group.visible = true;
    const ground = this.localPatch.groundHeightAtLocal(this.camera.position.x, this.camera.position.z);
    this.altitude = Math.max(0, this.camera.position.y - ground);
    this.curvature = smoothstep(
      this.config.localPatch.flatUntilAltitudeMeters,
      this.config.localPatch.fullyCurvedAltitudeMeters,
      this.altitude,
    );
    const farOpacity = smoothstep(
      this.config.localPatch.farPlanetFadeStartMeters,
      this.config.localPatch.farPlanetFadeEndMeters,
      this.altitude,
    );
    const patchOpacity = 1 - smoothstep(
      this.config.localPatch.farPlanetFadeEndMeters * 0.9,
      this.config.localPatch.farPlanetFadeEndMeters * 1.35,
      this.altitude,
    );
    this.localPatch.update(this.curvature, patchOpacity, elapsed);
    this.scene.background = new THREE.Color("#86b9d1");
    this.scene.fog = new THREE.FogExp2("#9fc8dc", THREE.MathUtils.lerp(0.00075, 0.00008, this.curvature));

    const active = this.getActivePlanet();
    for (const runtime of this.planets.values()) {
      const isActive = runtime.definition.id === active.definition.id;
      runtime.visual.group.visible = isActive && farOpacity > 0.005;
      if (!isActive) continue;
      const basis = new THREE.Matrix4().makeBasis(this.localPatch.frame.east, this.localPatch.frame.normal, this.localPatch.frame.north);
      const globalToLocal = basis.clone().invert();
      const translation = new THREE.Matrix4().makeTranslation(
        -this.localPatch.frame.anchorPoint.x,
        -this.localPatch.frame.anchorPoint.y,
        -this.localPatch.frame.anchorPoint.z,
      );
      runtime.visual.group.matrixAutoUpdate = false;
      runtime.visual.group.matrix.copy(globalToLocal.multiply(translation));
      runtime.visual.group.matrixWorldNeedsUpdate = true;
      runtime.visual.surface.material.transparent = true;
      runtime.visual.surface.material.opacity = farOpacity;
      runtime.visual.ocean.material.opacity = farOpacity * 0.7;
      runtime.visual.atmosphere.material.uniforms.uOpacity!.value = farOpacity * 0.38;
    }
  }

  private updateNetwork(now: number): void {
    const active = this.getActivePlanet();
    const sectorWire = this.observer.wire();
    let surfacePoint: [number, number, number] = [0, 0, 0];
    if (this.mode === "surface") {
      const groundPoint = this.localPatch.surfacePointForOffset(this.camera.position.x, this.camera.position.z);
      const normal = groundPoint.clone().normalize();
      const global = groundPoint.addScaledVector(normal, this.altitude);
      surfacePoint = [global.x, global.y, global.z];
    }
    this.network.update(now, {
      mode: this.mode,
      planetId: active.definition.id,
      sector: sectorWire.sector,
      local: sectorWire.local,
      surfacePoint,
      yaw: this.yaw,
      pitch: this.pitch,
    });
  }

  private updateRemotePlayers(): void {
    for (const [id, mesh] of this.remoteMeshes) {
      const player = this.players.get(id);
      if (!player || id === this.network.id) {
        mesh.visible = false;
        continue;
      }
      if (this.mode === "space" && player.mode === "space") {
        const position = new SectorPosition(player.sector, player.local, this.config.sectorSizeMeters);
        mesh.position.copy(position.relativeTo(this.observer));
        mesh.scale.setScalar(80);
        mesh.visible = true;
      } else if (this.mode === "surface" && player.mode === "surface" && player.planetId === this.activePlanetId) {
        mesh.position.copy(this.localPatch.planetToLocal(new THREE.Vector3(...player.surfacePoint)));
        mesh.scale.setScalar(1);
        mesh.visible = true;
      } else mesh.visible = false;
    }
  }

  private updatePlacedBlocks(): void {
    for (const [id, mesh] of this.blockMeshes) {
      const block = this.blocks.get(id);
      if (!block || this.mode !== "surface" || block.planetId !== this.activePlanetId) {
        mesh.visible = false;
        continue;
      }
      mesh.position.copy(this.localPatch.planetToLocal(new THREE.Vector3(...block.position)));
      mesh.visible = mesh.position.length() < 1_000;
    }
  }

  private handleMessage(message: ServerMessage): void {
    if (message.type === "welcome") {
      for (const player of message.players) this.upsertPlayer(player);
      for (const block of message.blocks) this.upsertBlock(block);
    } else if (message.type === "snapshot") {
      const activeIds = new Set(message.players.map((player) => player.id));
      for (const player of message.players) this.upsertPlayer(player);
      for (const id of this.players.keys()) if (!activeIds.has(id)) this.removePlayer(id);
    } else if (message.type === "player_joined") this.upsertPlayer(message.player);
    else if (message.type === "player_left") this.removePlayer(message.id);
    else if (message.type === "block_put") this.upsertBlock(message.block);
    else if (message.type === "block_remove") this.removeBlockById(message.id);
  }

  private upsertPlayer(player: PlayerWire): void {
    this.players.set(player.id, player);
    if (player.id === this.network.id || this.remoteMeshes.has(player.id)) return;
    const material = new THREE.MeshStandardMaterial({ color: "#72d9ff", emissive: "#173b4b", roughness: 0.6 });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2.4, 1.2), material);
    mesh.name = `remote-player:${player.id}`;
    this.remoteMeshes.set(player.id, mesh);
    this.remoteRoot.add(mesh);
  }

  private removePlayer(id: string): void {
    this.players.delete(id);
    const mesh = this.remoteMeshes.get(id);
    if (mesh) {
      this.remoteRoot.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.remoteMeshes.delete(id);
    }
  }

  private upsertBlock(block: PlacedBlockWire): void {
    this.blocks.set(block.id, block);
    let mesh = this.blockMeshes.get(block.id);
    if (!mesh) {
      const transparent = block.block === "glass";
      mesh = new THREE.Mesh(
        new THREE.BoxGeometry(4, 4, 4),
        new THREE.MeshStandardMaterial({
          color: BLOCK_COLORS[block.block],
          transparent,
          opacity: transparent ? 0.52 : 1,
          roughness: transparent ? 0.2 : 0.9,
        }),
      );
      mesh.userData.blockId = block.id;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.blockMeshes.set(block.id, mesh);
      this.blockRoot.add(mesh);
    }
  }

  private removeBlockById(id: string): void {
    this.blocks.delete(id);
    const mesh = this.blockMeshes.get(id);
    if (mesh) {
      this.blockRoot.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.blockMeshes.delete(id);
    }
  }

  private placeBlock(): void {
    if (this.curvature > 0.25) {
      this.toast("Строительство доступно в локальном плоском режиме");
      return;
    }
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.intersectObject(this.localPatch.terrainMesh, false)[0];
    if (!hit) return;
    const local = hit.point.clone().add(hit.face?.normal.clone().multiplyScalar(2.1) ?? new THREE.Vector3(0, 2.1, 0));
    const global = this.localPatch.localToPlanet(local);
    const block: PlacedBlockWire = {
      id: `${this.network.id || "local"}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      planetId: this.activePlanetId,
      position: [global.x, global.y, global.z],
      block: this.selectedBlock,
      owner: this.network.id,
    };
    this.upsertBlock(block);
    this.network.putBlock(block);
  }

  private removeBlock(): void {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(Array.from(this.blockMeshes.values()).filter((mesh) => mesh.visible), false);
    const mesh = hits[0]?.object as THREE.Mesh | undefined;
    const id = String(mesh?.userData.blockId ?? "");
    if (!id) return;
    this.removeBlockById(id);
    this.network.removeBlock(id);
  }

  private updateFps(delta: number): void {
    this.frameCounter += 1;
    this.frameAccumulator += delta;
    if (this.frameAccumulator >= 0.5) {
      this.fps = this.frameCounter / this.frameAccumulator;
      this.frameCounter = 0;
      this.frameAccumulator = 0;
    }
  }

  private updateHud(): void {
    const active = this.getActivePlanet();
    this.planetName.textContent = active.definition.name;
    this.planetMode.textContent = this.mode.toUpperCase();
    const sector = this.observer.sector;
    const local = this.observer.local;
    this.statusLines.innerHTML = [
      `<span>mode</span><b>${this.mode}</b>`,
      `<span>planet</span><b>${active.definition.name}</b>`,
      `<span>sector</span><b>${sector.x | 0}, ${sector.y | 0}, ${sector.z | 0}</b>`,
      `<span>local</span><b>${local.x.toFixed(0)}, ${local.y.toFixed(0)}, ${local.z.toFixed(0)} m</b>`,
      `<span>altitude</span><b>${this.altitude.toFixed(0)} m</b>`,
      `<span>curvature</span><b>${(this.curvature * 100).toFixed(0)}%</b>`,
      `<span>speed</span><b>${Math.round(this.speed)} m/s</b>`,
      `<span>fps / rtt</span><b>${this.fps.toFixed(0)} / ${this.network.rttMs.toFixed(0)} ms</b>`,
    ].join("");
  }

  private publishDebug(): void {
    const active = this.getActivePlanet();
    const wire = this.observer.wire();
    window.__PLANET_EXPERIMENT__ = {
      ready: this.ready,
      mode: this.mode,
      activePlanet: active.definition.id,
      sector: wire.sector,
      localOffset: wire.local,
      altitude: this.altitude,
      curvature: this.curvature,
      localChunks: this.localPatch.logicalChunkCount,
      planetMeshes: this.planets.size,
      players: this.players.size,
      blocks: this.blocks.size,
      fps: this.fps,
      rttMs: this.network.rttMs,
      webglError: this.contextLost ? this.renderer.getContext().CONTEXT_LOST_WEBGL : this.renderer.getContext().getError(),
      contextLost: this.contextLost,
      jumpToPlanet: (id) => this.jumpToPlanet(id),
      enterSurface: () => this.enterSurface(),
      enterSpace: () => this.enterSpace(),
      setDemoAltitude: (altitude) => this.setDemoAltitude(altitude),
      toggleGrid: () => this.toggleGrid(),
      setPaused: (paused) => this.setPaused(paused),
      renderOnce: () => this.renderOnce(),
    };
  }

  private resize(): void {
    const width = Math.max(1, innerWidth);
    const height = Math.max(1, innerHeight);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private getActivePlanet(): PlanetRuntime {
    return this.planets.get(this.activePlanetId) ?? Array.from(this.planets.values())[0]!;
  }

  private toast(message: string): void {
    this.toastElement.textContent = message;
    this.toastElement.classList.add("visible");
    window.setTimeout(() => this.toastElement.classList.remove("visible"), 1_600);
  }
}
